import React, { useRef, useState, useEffect } from "react";
import images from "../../assets/images";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../styles/ag-grid-custom.css";
import { ColDef } from "ag-grid-community";
import { ModuleRegistry } from "ag-grid-community";
import { CellStyleModule, PaginationModule } from "ag-grid-community";
import { Search } from "lucide-react";
import ActionModal from "./components/ActionModal";
import { usePremisesStore, useRegionStore } from "../../store/store";
import { generateIncidentExcel, formatDate } from "./utils/utils";
import { toast } from "react-toastify";
import { Dropdown } from "../../components/dropdown/Dropdown";
import PermissionWrapper from "../../components/PermissionWrapper/PermissionWrapper";
import { INCIDENT_REPORTS } from "../../constants/modules";
import { Incident } from "./types/types";
import {
  fetchIncidentActionsWithQuery,
  fetchRCAData,
} from "./services/service";
import { usePermissions } from "../../hooks/usePermission";
import { useResolvedIncidentsQuery, useInvalidateIncidents } from "./hooks/useIncidentQueries";

ModuleRegistry.registerModules([CellStyleModule, PaginationModule]);

const Report = () => {
  const [isRCAModalOpen, setIsRCAModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<Incident | null>(null);
  const [isDownload, setIsDownload] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [rcaId, setRcaId] = useState<string | null>(null);
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const gridRef = useRef<any>(null);

  const invalidateIncidents = useInvalidateIncidents();
  const { fetchPremises } = usePremisesStore();
  const { fetchRegions } = useRegionStore();
  const { canRead } = usePermissions();

  useEffect(() => {
    fetchPremises();
    fetchRegions();
  }, [fetchPremises, fetchRegions]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: rowData = [], isLoading: loading } = useResolvedIncidentsQuery({
    debouncedSearchTerm,
    selectedPremises,
    selectedRegion,
  });

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredData = Array.isArray(rowData)
    ? rowData.filter((incident: any) => {
      const matchesSearch =
        incident?.incidentNumber
          ?.toString()
          .toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase()) ||
        incident?.department
          ?.toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase()) ||
        incident?.reportedBy
          ?.toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase());

      const matchesPremises = selectedPremises
        ? incident?.hospitalName === selectedPremises
        : true;
      const matchesRegion = selectedRegion
        ? incident?.region === selectedRegion
        : true;

      return matchesSearch && matchesPremises && matchesRegion;
    })
    : [];

  const handleViewRCA = (data: any) => {
    if (data.status === "Resolved") {
      setSelectedRowData(data);
      setRcaId(data.hasRca ? data.rcaId : null);
      setIsRCAModalOpen(true);
    }
  };

  const handleDownloadReport = async (data: Incident) => {
    try {
      let sendModalData = null;
      let rcaData = null;

      if (
        data.status.toLowerCase() === "pending" ||
        data.status.toLowerCase() === "resolved"
      ) {
        try {
          const sendResponse = await fetchIncidentActionsWithQuery({
            incident: data.id,
          });
          sendModalData = sendResponse.data?.[0] || null;
        } catch (actionError) {
          console.warn("No action data found for incident:", data.id);
          // Continue execution even if action data is not found
        }
      }

      if (data.status.toLowerCase() === "resolved") {
        try {
          const rcaResponse = await fetchRCAData({ incident: data.id });
          rcaData = rcaResponse.data?.[0] || null;
        } catch (rcaError) {
          console.warn("No RCA data found for incident:", data.id);
          // Continue execution even if RCA data is not found
        }
      }

      generateIncidentExcel(data, sendModalData as any, rcaData as any);
    } catch (error) {
      console.error("Error preparing Excel download:", error);
      toast.error("Failed to generate Excel file");
    }
  };

  const handleActionComplete = (actionType?: string) => {
    if (actionType === "resolved" || actionType === "updated") {
      invalidateIncidents();
    }
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "Incident Number",
      field: "incidentNumber",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 5px " },
    },
    {
      headerName: "Report Date",
      field: "reportDate",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0 " },
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Department",
      field: "department",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0 " },
    },
    {
      headerName: "Event Type",
      field: "moduleSpecificData.incidentEventType",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0 " },
    },
    {
      headerName: "Incident Date",
      field: "incidentDate",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0 " },
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Closure Date",
      field: "closureDate",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0 " },
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Reported By",
      field: "reportedBy",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0 " },
    },
    {
      headerName: "Incident Status",
      field: "status",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: (params) => {
        let color = "black";
        let borderStyle = "none";

        switch (params.value) {
          case "Resolved":
            color = "#2fb4ae";
            borderStyle = "1px solid #2fb4ae";
            break;
          case "Open":
            color = "#eb665c";
            borderStyle = "1px solid #eb665c";
            break;
          case "Pending":
            color = "#fa9c4c";
            borderStyle = "1px solid #fa9c4c";
            break;
        }

        return {
          color: color,
          fontWeight: "semibold",
          padding: "10px 0 0 0",
          border: borderStyle,
          borderRadius: "4px",
        };
      },
    },
    {
      headerName: "Corrective Action",
      field: "implementedCorrectiveActions",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      valueFormatter: (params) => params.value ? "Completed" : "Pending",
    },
    {
      headerName: "RCA Status",
      field: "rcaStatus",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: (params) => {
        let color = "#999";
        if (params.value === "Completed") color = "#4CAF50";
        else if (params.value === "In Progress") color = "#FF9800";
        else if (params.value === "Draft") color = "#2196F3";
        return { color, fontWeight: "600", padding: "10px 0 0 0" };
      },
      valueFormatter: (params) => {
        if (params.value === "Completed") return "Completed";
        if (params.value === "In Progress") return "In Progress";
        if (params.value === "Draft") return "Draft";
        return "Not Started";
      },
    },
    {
      headerName: "Action",
      field: "status",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellStyle: { padding: "10px 0" },
      cellRenderer: (params: any) => {
        return (
          <div className="flex items-center gap-2 justify-center">
            {params.data.status === "Resolved" && (
              <button
                className="p-1 hover:scale-105 transition-transform"
                onClick={() => handleViewRCA(params.data)}
                title="View RCA (Read-only)"
              >
                <img className="w-5 h-5" src={images.viewIcon} alt="View RCA" />
              </button>
            )}
            <button
              className="p-1 hover:scale-105 transition-transform"
              onClick={() => handleDownloadReport(params.data)}
            >
              <img
                className="w-5 h-5"
                src={images.downloadIcon}
                alt="Download"
              />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <PermissionWrapper module={INCIDENT_REPORTS}>
      <>
        <div className="mt-16 px-2 py-4 space-y-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold">Reports</h1>
            <div className="flex items-center gap-4">
              {/* {canRead(INCIDENT_REPORTS) && (
              <button
                className="flex items-center justify-center font-semibold gap-3 bg-[#4f5762] text-white px-4 py-2 rounded-full"
                onClick={() => {
                  setSelectedRowData(null);
                  setIsDownload(true);
                }}
              >
                <img src={images.downloadIcon} alt="" className="w-5 h-5 text-white" />
                BULK EXPORT
              </button>
            )} */}
              <Dropdown
                selectedValue={selectedRegion}
                onChange={setSelectedRegion}
                type="region"
              />
              <Dropdown
                selectedValue={selectedPremises}
                onChange={setSelectedPremises}
                type="premises"
                selectedRegion={selectedRegion}
              />
            </div>
          </div>

          <div className="bg-white w-full h-full drop-shadow-[0_0px_10px_rgba(0,0,0,0.25)]">
            <div className="p-6">
              <div className="flex justify-between ">
                <div className="flex gap-5 items-center">
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search by Incident Number, Department, or Reporter"
                      className="border-2 border-[#4f5762] w-[400px] px-10 py-2 rounded placeholder:font-semibold placeholder:text-[#4e545d]"
                      value={searchTerm}
                      onChange={handleSearch}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="ag-theme-alpine">
              {loading ? (
                <div className="text-center py-4">Loading reports...</div>
              ) : (
                <AgGridReact
                  ref={gridRef}
                  rowData={filteredData}
                  columnDefs={columnDefs}
                  rowHeight={50}
                  headerHeight={64}
                  paginationPageSize={20}
                  pagination={true}
                  domLayout="autoHeight"
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                  }}
                  getRowStyle={() => ({
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    marginBottom: "8px",
                    borderRadius: "4px",
                    backgroundColor: "white",
                  })}
                />
              )}
            </div>
          </div>
        </div>

        {(isRCAModalOpen || isDownload) && (
          <ActionModal
            showModal={false}
            showRCAModal={isRCAModalOpen}
            showDownload={isDownload}
            setShowRCAModal={setIsRCAModalOpen}
            setShowModal={() => { }}
            setShowDownload={setIsDownload}
            data={selectedRowData}
            setSelectedRowData={setSelectedRowData}
            onActionComplete={handleActionComplete}
            mode="view"
            actionId={undefined}
            setRcaId={setRcaId}
            rcaId={rcaId || null}
            moduleKey={INCIDENT_REPORTS}
          />
        )}
      </>
    </PermissionWrapper>
  );
};

export default Report;
